'use client';

import { useState } from 'react';
import { StepName } from './steps/StepName';
import { useWizard } from './useWizard';
import { StepCouple } from './steps/StepCouple';
import { WizardData } from './store';
import { StepRelationship } from './steps/StepRelationship';
import { StepKnownSince } from './steps/StepKnownSince';
import { StepMemories } from './steps/StepMemories';
import { StepStyle } from './steps/StepStyle';
import { StepEmail } from './steps/StepEmail';
import { StepIntroduction } from './steps/StepIntroduction';
import { logEvent } from 'firebase/analytics';
import { analytics } from '@/firebase';

type Props = {
  onFinish: (data: WizardData) => Promise<void>;
};

export default function Questionnaire(props: Props) {
  const [activeSection, setActiveSection] = useState(0);
  const { data, setData, resetData } = useWizard();

  const onSubmit = (data: Partial<WizardData>) => {
    logEvent(analytics, `wizard_step_${activeSection + 1}_completed`);
    setData(data);
    setActiveSection(activeSection + 1);
  };

  const finish = (emailData: { email: string }) => {
    setData(emailData);
    props.onFinish({ ...data, ...emailData });
    if (import.meta.env.PROD) {
      resetData();
    }
    logEvent(analytics, `wizard_completed`);
  };

  return (
    <div className="w-[880px] max-w-[90vw] mx-auto p-4 space-y-4">
      <StepIntroduction
        isActive={activeSection === 0}
        isDone={activeSection > 0}
        onNext={() => setActiveSection(1)}
        onActivate={() => setActiveSection(0)}
      />
      <StepName
        isActive={activeSection === 1}
        isDone={activeSection > 1}
        onActivate={() => setActiveSection(1)}
        defaultData={{ name: data.name }}
        onSubmit={onSubmit}
      />
      <StepCouple
        isActive={activeSection === 2}
        isDone={activeSection > 2}
        onActivate={() => setActiveSection(2)}
        defaultData={{
          bride: data.bride,
          groom: data.groom,
          bride_pronoun: data.bride_pronoun,
          groom_pronoun: data.groom_pronoun,
        }}
        onSubmit={onSubmit}
      />
      <StepRelationship
        isActive={activeSection === 3}
        isDone={activeSection > 3}
        onActivate={() => setActiveSection(3)}
        defaultData={{
          relationship: data.relationship,
        }}
        onSubmit={onSubmit}
      />
      <StepKnownSince
        isActive={activeSection === 4}
        isDone={activeSection > 4}
        onActivate={() => setActiveSection(4)}
        defaultData={{
          known_since: data.known_since,
        }}
        onSubmit={onSubmit}
      />
      <StepMemories
        isActive={activeSection === 5}
        isDone={activeSection > 5}
        onActivate={() => setActiveSection(5)}
        defaultData={{
          memories: data.memories,
        }}
        onSubmit={onSubmit}
      />
      <StepStyle
        isActive={activeSection === 6}
        isDone={activeSection > 6}
        onActivate={() => setActiveSection(6)}
        defaultData={{
          style: data.style,
        }}
        onSubmit={onSubmit}
      />
      <StepEmail
        isActive={activeSection === 7}
        isDone={activeSection > 7}
        onActivate={() => setActiveSection(7)}
        defaultData={{
          email: data.email,
        }}
        onSubmit={finish}
      />
    </div>
  );
}
